import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { startAuthentication } from "@simplewebauthn/browser";

import {
  login as apiLogin,
  register as apiRegister,
  getPasskeyAuthenticationOptions,
  verifyPasskeyAuthentication,
} from "@/api/auth";
import { getProfile } from "@/api/users";
import { clearTokens, getToken, setTokens } from "@/api/client";
import type {
  AuthContextValue,
  AuthTokens,
  LoginCredentials,
  RegisterData,
  User,
} from "@/types";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const AuthContext = createContext<AuthContextValue | null>(null);

AuthContext.displayName = "AuthContext";

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokensState] = useState<AuthTokens | null>(() => {
    const token = localStorage.getItem("cliqo_token");
    const refreshToken = localStorage.getItem("cliqo_refresh_token");
    const refreshTokenExpiresAt = localStorage.getItem(
      "cliqo_refresh_token_expires",
    );
    if (token && refreshToken && refreshTokenExpiresAt) {
      return { token, refreshToken, refreshTokenExpiresAt };
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Prevent double-hydration in React StrictMode
  const hydrated = useRef(false);

  // -------------------------------------------------------------------------
  // Persist tokens to localStorage and update local state
  // -------------------------------------------------------------------------

  const persistTokens = useCallback((t: AuthTokens) => {
    setTokens(t.token, t.refreshToken, t.refreshTokenExpiresAt);
    setTokensState(t);
  }, []);

  // -------------------------------------------------------------------------
  // Hydrate user from the API using the stored token
  // -------------------------------------------------------------------------

  const hydrate = useCallback(async (): Promise<void> => {
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const profile = await getProfile();
      setUser(profile);
      // Re-sync tokens state from storage in case it was set before the
      // provider mounted (e.g. after a hard refresh or OAuth redirect).
      const storedToken = localStorage.getItem("cliqo_token");
      const storedRefresh = localStorage.getItem("cliqo_refresh_token");
      const storedExpires = localStorage.getItem("cliqo_refresh_token_expires");
      if (storedToken && storedRefresh && storedExpires) {
        setTokensState({
          token: storedToken,
          refreshToken: storedRefresh,
          refreshTokenExpiresAt: storedExpires,
        });
      }
    } catch {
      // Token is invalid or expired — clear everything so the user is sent to /login
      clearTokens();
      setTokensState(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // -------------------------------------------------------------------------
  // On mount: hydrate once
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    void hydrate();
  }, [hydrate]);

  // -------------------------------------------------------------------------
  // login
  // -------------------------------------------------------------------------

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      const data = await apiLogin(credentials);
      persistTokens(data);

      const profile = await getProfile();
      setUser(profile);
    },
    [persistTokens],
  );

  // -------------------------------------------------------------------------
  // loginWithPasskey — passwordless WebAuthn sign-in
  // -------------------------------------------------------------------------

  const loginWithPasskey = useCallback(async (): Promise<void> => {
    const { flowId, options } = await getPasskeyAuthenticationOptions();
    const credential = await startAuthentication({ optionsJSON: options });
    const data = await verifyPasskeyAuthentication(flowId, credential);
    persistTokens(data);

    const profile = await getProfile();
    setUser(profile);
  }, [persistTokens]);

  // -------------------------------------------------------------------------
  // register
  // -------------------------------------------------------------------------

  const register = useCallback(async (data: RegisterData): Promise<void> => {
    // Registration does not log the user in automatically — they must
    // verify their email first. We simply call the API and let the page
    // handle the success state (e.g. show a "check your inbox" message).
    await apiRegister(data);
  }, []);

  // -------------------------------------------------------------------------
  // logout
  // -------------------------------------------------------------------------

  const logout = useCallback((): void => {
    clearTokens();
    setTokensState(null);
    setUser(null);
    // Hard-navigate so all in-memory state (query cache, etc.) is cleared.
    window.location.href = "/login";
  }, []);

  // -------------------------------------------------------------------------
  // refreshUser — re-fetch profile from the API
  // -------------------------------------------------------------------------

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const profile = await getProfile();
      setUser(profile);
    } catch {
      logout();
    }
  }, [logout]);

  // -------------------------------------------------------------------------
  // updateUser — optimistic local update (no API call)
  // -------------------------------------------------------------------------

  const updateUser = useCallback((data: Partial<User>): void => {
    setUser((prev) => (prev ? { ...prev, ...data } : prev));
  }, []);

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------

  const isAuthenticated = user !== null && getToken() !== null;

  // -------------------------------------------------------------------------
  // Context value (memoised to avoid unnecessary re-renders)
  // -------------------------------------------------------------------------

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      tokens,
      isLoading,
      isAuthenticated,
      login,
      loginWithPasskey,
      register,
      logout,
      refreshUser,
      updateUser,
    }),
    [
      user,
      tokens,
      isLoading,
      isAuthenticated,
      login,
      loginWithPasskey,
      register,
      logout,
      refreshUser,
      updateUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
